import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  FileText,
  DollarSign,
  Calendar,
  Target,
  TrendingUp,
  CheckCircle,
  XCircle,
  Eye,
  Download,
  AlertCircle,
  X,
  Plus,
  Users,
  RefreshCw,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { adminApi, documentsApi } from '../../api';
import { ApplicationStatus } from '../../types';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { StatusBadge } from '../../components/StatusBadge';
import { DocumentThumbnail } from '../../components/DocumentThumbnail';
import { DocumentLightbox } from '../../components/DocumentLightbox';
import { Button } from '../../components/Button';
import { formatCurrency } from '../../utils/currency';
import { BASE_URL } from '../../api/client';

export const ApplicationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [application, setApplication] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [approvalNotes, setApprovalNotes] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [disbursementDate, setDisbursementDate] = useState('');

  // Document state
  const [documents, setDocuments] = useState<any[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [lightboxDocument, setLightboxDocument] = useState<any>(null);
  const [documentVerificationNotes, setDocumentVerificationNotes] = useState('');

  // Document request state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [documentRequests, setDocumentRequests] = useState<any[]>([]);
  const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>([]);
  const [requestMessage, setRequestMessage] = useState('');

  // Guarantor state
  const [guarantors, setGuarantors] = useState<any[]>([]);
  const [guarantorsLoading, setGuarantorsLoading] = useState(false);

  useEffect(() => {
    if (!id) {
      navigate('/admin/applications');
      return;
    }

    fetchApplication();
  }, [id]);

  const fetchGuarantors = async () => {
    if (!id) return;

    setGuarantorsLoading(true);
    try {
      const response = await adminApi.getGuarantorsForApplication(id);
      if (response.success && response.data) {
        setGuarantors(response.data.guarantors || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch guarantors:', err);
    } finally {
      setGuarantorsLoading(false);
    }
  };

  const fetchApplication = async () => {
    try {
      const response = await adminApi.getApplicationById(id!);
      if (response.success && response.data) {
        setApplication(response.data.application);
        // Set documents from application data (already included in getApplicationById)
        if (response.data.documents) {
          setDocuments(response.data.documents);
        }
        // Set default interest rate to product's min rate
        if (response.data.application.min_interest_rate) {
          setInterestRate(response.data.application.min_interest_rate);
        }
        // Fetch document requests for this application
        fetchDocumentRequests();
        // Fetch guarantor requests for this application
        fetchGuarantors();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load application details');
    } finally {
      setLoading(false);
      setDocumentsLoading(false);
    }
  };

  const fetchDocuments = async () => {
    // Documents are now loaded from the application data
    // This function is kept for compatibility but just re-fetches the application
    fetchApplication();
  };

  const handleVerifyDocument = async (documentId: string, isVerified: boolean) => {
    setActionLoading(true);
    setError('');

    try {
      const response = await adminApi.verifyDocument(
        documentId,
        isVerified,
        documentVerificationNotes || undefined
      );
      if (response.success) {
        setSuccessMessage(
          isVerified ? 'Document approved successfully' : 'Document rejected successfully'
        );
        setSelectedDocument(null);
        setDocumentVerificationNotes('');
        fetchDocuments();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify document');
    } finally {
      setActionLoading(false);
    }
  };

  const getDocumentStatusBadge = (doc: any) => {
    if (doc.is_verified === true) {
      return <span className="badge badge-success">Verified</span>;
    } else if (doc.is_verified === false) {
      return <span className="badge badge-danger">Rejected</span>;
    }
    return <span className="badge badge-warning">Pending Review</span>;
  };

  const fetchDocumentRequests = async () => {
    try {
      const response = await adminApi.getDocumentRequests(id!);
      if (response.success && response.data) {
        setDocumentRequests(response.data.requests);
      }
    } catch (err: any) {
      console.error('Failed to fetch document requests:', err);
    }
  };

  const handleRequestDocuments = async () => {
    if (selectedDocTypes.length === 0) {
      setError('Please select at least one document type');
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      const response = await adminApi.requestDocuments(
        id!,
        selectedDocTypes,
        requestMessage || undefined
      );
      if (response.success) {
        setSuccessMessage('Document request sent successfully');
        setShowRequestModal(false);
        setSelectedDocTypes([]);
        setRequestMessage('');
        fetchDocumentRequests();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to request documents');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleDocumentType = (docType: string) => {
    setSelectedDocTypes(prev =>
      prev.includes(docType)
        ? prev.filter(t => t !== docType)
        : [...prev, docType]
    );
  };

  const handleResendGuarantorRequest = async (requestId: string) => {
    setActionLoading(true);
    setError('');

    try {
      const response = await adminApi.resendGuarantorRequest(requestId);
      if (response.success) {
        setSuccessMessage('Guarantor request resent successfully');
        fetchGuarantors();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend guarantor request');
    } finally {
      setActionLoading(false);
    }
  };

  const getGuarantorStatusBadge = (guarantor: any) => {
    const now = new Date();
    const expiresAt = new Date(guarantor.expires_at);

    if (guarantor.submitted_at) {
      return <span className="badge badge-success"><CheckCircle2 size={14} /> Submitted</span>;
    } else if (now > expiresAt) {
      return <span className="badge badge-danger"><Clock size={14} /> Expired</span>;
    } else {
      return <span className="badge badge-warning"><Clock size={14} /> Pending</span>;
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    setError('');

    try {
      const response = await adminApi.approveApplication(id!, approvalNotes);
      if (response.success) {
        setSuccessMessage('Application approved successfully');
        fetchApplication();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to approve application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason) {
      setError('Please provide a reason for declining');
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      const response = await adminApi.declineApplication(id!, declineReason);
      if (response.success) {
        setSuccessMessage('Application declined successfully');
        fetchApplication();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to decline application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisburse = async () => {
    if (!interestRate) {
      setError('Please provide an interest rate');
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      const response = await adminApi.disburseLoan(
        id!,
        parseFloat(interestRate),
        disbursementDate || undefined
      );
      if (response.success) {
        setSuccessMessage('Loan disbursed successfully');
        fetchApplication();
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to disburse loan');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!application) {
    return (
      <div className="error-state">
        <p>Application not found</p>
      </div>
    );
  }

  return (
    <div className="application-details-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Application Details</h1>
          <p>Review and process loan application request</p>
        </div>
        <Button onClick={() => navigate('/admin/applications')} variant="secondary" icon={<ArrowLeft size={20} />}>
          Back to Applications
        </Button>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {successMessage && (
        <div className="success-message">
          <span>{successMessage}</span>
        </div>
      )}

      <div className="application-info-card">
        <div className="card-header">
          <h2>{application.application_reference}</h2>
          <StatusBadge status={application.status} size="lg" />
        </div>

        <div className="application-info-grid">
          <div className="info-section">
            <h3><User size={20} style={{ display: 'inline', marginRight: '8px' }} />Customer Information</h3>
            <div className="info-item">
              <div className="info-icon">
                <User size={18} />
              </div>
              <div>
                <span className="label">Name</span>
                <span className="value">
                  {application.customer_name ||
                    `${application.first_name || ''} ${application.last_name || ''}`}
                </span>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <Mail size={18} />
              </div>
              <div>
                <span className="label">Email</span>
                <span className="value">{application.email || 'N/A'}</span>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <Phone size={18} />
              </div>
              <div>
                <span className="label">Phone</span>
                <span className="value">{application.phone || 'N/A'}</span>
              </div>
            </div>
            {application.user_id && (
              <div className="info-item" style={{ marginTop: '1rem' }}>
                <Button
                  onClick={() => navigate(`/admin/customers/${application.user_id}/360`)}
                  variant="secondary"
                  fullWidth
                  icon={<Eye size={20} />}
                >
                  View Customer 360
                </Button>
              </div>
            )}
          </div>

          <div className="info-section">
            <h3><FileText size={20} style={{ display: 'inline', marginRight: '8px' }} />Loan Information</h3>
            <div className="info-item">
              <div className="info-icon">
                <FileText size={18} />
              </div>
              <div>
                <span className="label">Product</span>
                <span className="value">{application.product_name || 'N/A'}</span>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <DollarSign size={18} />
              </div>
              <div>
                <span className="label">Amount Requested</span>
                <span className="value">
                  {formatCurrency(application.amount_requested)}
                </span>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <Calendar size={18} />
              </div>
              <div>
                <span className="label">Loan Term</span>
                <span className="value">{application.loan_term_months} months</span>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <Target size={18} />
              </div>
              <div>
                <span className="label">Purpose</span>
                <span className="value">{application.purpose || 'N/A'}</span>
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">
                <TrendingUp size={18} />
              </div>
              <div>
                <span className="label">Monthly Income</span>
                <span className="value">
                  {application.monthly_income
                    ? formatCurrency(application.monthly_income)
                    : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          <div className="info-section">
            <h3>Application Status</h3>
            <div className="info-item">
              <span className="label">Created:</span>
              <span className="value">
                {new Date(application.created_at).toLocaleDateString()}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Submitted:</span>
              <span className="value">
                {application.submitted_at
                  ? new Date(application.submitted_at).toLocaleDateString()
                  : 'Not submitted'}
              </span>
            </div>
            {application.approval_notes && (
              <div className="info-item">
                <span className="label">Approval Notes:</span>
                <span className="value">{application.approval_notes}</span>
              </div>
            )}
            {application.decline_reason && (
              <div className="info-item">
                <span className="label">Decline Reason:</span>
                <span className="value">{application.decline_reason}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Documents Section */}
      <div className="documents-card">
        <div className="card-header">
          <div>
            <h3>
              <FileText size={20} style={{ display: 'inline', marginRight: '8px' }} />
              Uploaded Documents
            </h3>
            {documents.length > 0 && (
              <span className="document-count">
                {documents.filter(d => d.is_verified === true).length} of {documents.length} verified
              </span>
            )}
          </div>
          {application?.status === ApplicationStatus.SUBMITTED && (
            <Button
              onClick={() => setShowRequestModal(true)}
              variant="secondary"
              size="sm"
              icon={<Plus size={18} />}
            >
              Request Documents
            </Button>
          )}
        </div>

        {documentsLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <LoadingSpinner />
          </div>
        ) : documents.length === 0 ? (
          <div className="empty-state">
            <AlertCircle size={48} />
            <p>No documents uploaded yet</p>
          </div>
        ) : (
          <div className="documents-list">
            {documents.map((doc) => (
              <div key={doc.document_id} className="document-item">
                <div className="document-info">
                  <DocumentThumbnail
                    fileName={doc.file_name}
                    filePath={doc.file_path}
                    mimeType={doc.mime_type}
                    onClick={() => setLightboxDocument(doc)}
                    size="medium"
                  />
                  <div className="document-details">
                    <h4>{doc.document_type.replace(/_/g, ' ').toUpperCase()}</h4>
                    <p className="document-filename">{doc.file_name}</p>
                    <p className="document-meta">
                      Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()} •{' '}
                      {(doc.file_size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                    {doc.verification_notes && (
                      <p className="verification-notes">
                        <strong>Notes:</strong> {doc.verification_notes}
                      </p>
                    )}
                  </div>
                </div>
                <div className="document-actions">
                  {getDocumentStatusBadge(doc)}
                  <Button
                    onClick={() => setSelectedDocument(doc)}
                    variant="secondary"
                    size="sm"
                    icon={<Eye size={16} />}
                  >
                    Review
                  </Button>
                  <Button
                    onClick={() => window.open(`${BASE_URL}/${doc.file_path}`, '_blank')}
                    variant="secondary"
                    size="sm"
                    icon={<Download size={16} />}
                  >
                    Download
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Document Review Modal */}
      {selectedDocument && (
        <div className="modal-overlay" onClick={() => setSelectedDocument(null)}>
          <div className="modal-content document-review-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Review Document</h3>
              <Button onClick={() => setSelectedDocument(null)} variant="ghost" size="sm">
                <X size={24} />
              </Button>
            </div>

            <div className="modal-body">
              <div className="document-preview">
                <h4>{selectedDocument.document_type.replace(/_/g, ' ').toUpperCase()}</h4>
                <p className="document-filename">{selectedDocument.file_name}</p>

                {selectedDocument.mime_type && selectedDocument.mime_type.startsWith('image/') ? (
                  <img
                    src={`${BASE_URL}/${selectedDocument.file_path}`}
                    alt={selectedDocument.document_type}
                    style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}
                  />
                ) : selectedDocument.mime_type === 'application/pdf' ? (
                  <iframe
                    src={`${BASE_URL}/${selectedDocument.file_path}`}
                    style={{ width: '100%', height: '500px', border: '1px solid #ddd' }}
                    title={selectedDocument.document_type}
                  />
                ) : (
                  <div className="unsupported-preview">
                    <FileText size={48} />
                    <p>Preview not available for this file type</p>
                    <Button
                      onClick={() => window.open(`${BASE_URL}/${selectedDocument.file_path}`, '_blank')}
                      variant="secondary"
                      icon={<Download size={16} />}
                    >
                      Download to view
                    </Button>
                  </div>
                )}
              </div>

              <div className="verification-form">
                <div className="form-group">
                  <label htmlFor="docVerificationNotes">Verification Notes</label>
                  <textarea
                    id="docVerificationNotes"
                    value={documentVerificationNotes}
                    onChange={(e) => setDocumentVerificationNotes(e.target.value)}
                    rows={3}
                    placeholder="Add notes about this document (required for rejection)..."
                    disabled={actionLoading}
                  />
                </div>

                <div className="modal-actions">
                  <Button
                    onClick={() => handleVerifyDocument(selectedDocument.document_id, false)}
                    variant="danger"
                    disabled={actionLoading || !documentVerificationNotes}
                    loading={actionLoading}
                    icon={<XCircle size={20} />}
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleVerifyDocument(selectedDocument.document_id, true)}
                    variant="success"
                    disabled={actionLoading}
                    loading={actionLoading}
                    icon={<CheckCircle size={20} />}
                  >
                    Approve
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Lightbox */}
      {lightboxDocument && (
        <DocumentLightbox
          document={lightboxDocument}
          onClose={() => setLightboxDocument(null)}
        />
      )}

      {/* Request Documents Modal */}
      {showRequestModal && (
        <div className="modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Request Additional Documents</h3>
              <Button onClick={() => setShowRequestModal(false)} variant="ghost" size="sm">
                <X size={24} />
              </Button>
            </div>

            <div className="modal-body">
              <p style={{ marginBottom: '1.5rem', color: 'var(--color-text-secondary)' }}>
                Select the documents you need from the customer:
              </p>

              <div className="document-types-checklist">
                {[
                  { id: 'national_id', label: 'Government-Issued ID' },
                  { id: 'bvn_slip', label: 'BVN Verification' },
                  { id: 'salary_slip', label: 'Proof of Income / Salary Slip' },
                  { id: 'bank_statement', label: 'Bank Statements' },
                  { id: 'utility_bill', label: 'Proof of Address / Utility Bill' },
                  { id: 'passport', label: 'Passport Photograph' },
                  { id: 'cac_certificate', label: 'CAC Certificate (Business Registration)' },
                  { id: 'tax_clearance', label: 'Tax Clearance Certificate' },
                  { id: 'business_plan', label: 'Business Plan' },
                  { id: 'lpo_document', label: 'LPO Document' },
                  { id: 'contract_document', label: 'Contract Award Letter' },
                  { id: 'invoice', label: 'Invoices' },
                  { id: 'other', label: 'Other Documents' },
                ].map((docType) => (
                  <label key={docType.id} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={selectedDocTypes.includes(docType.id)}
                      onChange={() => toggleDocumentType(docType.id)}
                      disabled={actionLoading}
                    />
                    <span>{docType.label}</span>
                  </label>
                ))}
              </div>

              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label htmlFor="requestMessage">Message to Customer</label>
                <textarea
                  id="requestMessage"
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  rows={4}
                  placeholder="Explain what documents are needed and why..."
                  disabled={actionLoading}
                />
                <small style={{ color: 'var(--color-text-tertiary)', marginTop: '0.5rem', display: 'block' }}>
                  This message will be sent to the customer with the document request.
                </small>
              </div>

              <div className="modal-actions">
                <Button
                  onClick={() => setShowRequestModal(false)}
                  variant="secondary"
                  disabled={actionLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRequestDocuments}
                  variant="primary"
                  disabled={actionLoading || selectedDocTypes.length === 0}
                  loading={actionLoading}
                >
                  Request {selectedDocTypes.length} Document(s)
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Requests History */}
      {documentRequests.length > 0 && (
        <div className="documents-card">
          <div className="card-header">
            <h3>Document Request History</h3>
          </div>
          <div className="document-requests-list">
            {documentRequests.map((request) => (
              <div key={request.request_id} className="document-request-item">
                <div className="request-info">
                  <div className="request-header">
                    <strong>Requested Documents:</strong>
                    <span className={`badge badge-${request.status === 'fulfilled' ? 'success' : request.status === 'partially_fulfilled' ? 'warning' : 'warning'}`}>
                      {request.status.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className="requested-doc-list">
                    {request.requested_documents.map((doc: string, idx: number) => (
                      <span key={idx} className="doc-chip">
                        {doc.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                  {request.request_message && (
                    <p className="request-message">
                      <strong>Message:</strong> {request.request_message}
                    </p>
                  )}
                  <div className="request-meta">
                    <span>Requested by: {request.requested_by_name || 'Unknown'}</span>
                    <span>Date: {new Date(request.created_at).toLocaleDateString()}</span>
                    {request.fulfilled_at && (
                      <span>Fulfilled: {new Date(request.fulfilled_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Guarantor Information Section */}
      {guarantorsLoading ? (
        <div className="documents-card">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <LoadingSpinner />
            <p>Loading guarantor information...</p>
          </div>
        </div>
      ) : guarantors.length > 0 && (
        <div className="documents-card">
          <div className="card-header">
            <div>
              <h3>
                <Users size={20} style={{ display: 'inline', marginRight: '8px' }} />
                Guarantor Authorization
              </h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
                {guarantors.filter(g => g.submitted_at).length} of {guarantors.length} guarantors have submitted their authorization
              </p>
            </div>
          </div>

          <div className="guarantors-list">
            {guarantors.map((guarantor, index) => (
              <div key={guarantor.request_id} className="guarantor-item">
                <div className="guarantor-info">
                  <div className="guarantor-header">
                    <h4>
                      <User size={18} style={{ display: 'inline', marginRight: '8px' }} />
                      Guarantor {index + 1}: {guarantor.guarantor_name}
                    </h4>
                    {getGuarantorStatusBadge(guarantor)}
                  </div>

                  <div className="guarantor-details">
                    <div className="info-row">
                      <div className="info-item">
                        <Mail size={16} />
                        <span>{guarantor.guarantor_email}</span>
                      </div>
                      <div className="info-item">
                        <Phone size={16} />
                        <span>{guarantor.guarantor_phone}</span>
                      </div>
                    </div>

                    {guarantor.submitted_at ? (
                      <div className="guarantor-submission-info">
                        <div className="info-row">
                          <div className="info-item">
                            <Calendar size={16} />
                            <span><strong>Submitted:</strong> {new Date(guarantor.submitted_at).toLocaleString()}</span>
                          </div>
                        </div>
                        {guarantor.guarantor_address && (
                          <div className="info-row">
                            <div className="info-item">
                              <strong>Address:</strong> {guarantor.guarantor_address}
                            </div>
                          </div>
                        )}
                        {guarantor.guarantor_occupation && (
                          <div className="info-row">
                            <div className="info-item">
                              <strong>Occupation:</strong> {guarantor.guarantor_occupation}
                            </div>
                          </div>
                        )}
                        {guarantor.guarantor_employer && (
                          <div className="info-row">
                            <div className="info-item">
                              <strong>Employer:</strong> {guarantor.guarantor_employer}
                            </div>
                          </div>
                        )}
                        {guarantor.relationship_to_borrower && (
                          <div className="info-row">
                            <div className="info-item">
                              <strong>Relationship:</strong> {guarantor.relationship_to_borrower}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="guarantor-pending-info">
                        <div className="info-row">
                          <div className="info-item">
                            <Clock size={16} />
                            <span><strong>Requested:</strong> {new Date(guarantor.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="info-item">
                            <Clock size={16} />
                            <span><strong>Expires:</strong> {new Date(guarantor.expires_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="guarantor-actions" style={{ marginTop: '1rem' }}>
                          <Button
                            onClick={() => handleResendGuarantorRequest(guarantor.request_id)}
                            variant="secondary"
                            size="sm"
                            disabled={actionLoading}
                            loading={actionLoading}
                            icon={<RefreshCw size={16} />}
                          >
                            Resend Request
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {application.status === ApplicationStatus.SUBMITTED && (
        <div className="action-card">
          <h3>Review Application</h3>

          <div className="form-section">
            <h4>Approve Application</h4>
            <div className="form-group">
              <label htmlFor="approvalNotes">Approval Notes</label>
              <textarea
                id="approvalNotes"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                disabled={actionLoading}
                rows={3}
                placeholder="Add any notes about the approval..."
              />
            </div>
            <Button
              onClick={handleApprove}
              variant="success"
              disabled={actionLoading}
              loading={actionLoading}
              icon={<CheckCircle size={20} />}
            >
              Approve Application
            </Button>
          </div>

          <div className="form-section">
            <h4>Decline Application</h4>
            <div className="form-group">
              <label htmlFor="declineReason">Decline Reason *</label>
              <textarea
                id="declineReason"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                disabled={actionLoading}
                rows={3}
                placeholder="Provide a reason for declining..."
                required
              />
            </div>
            <Button
              onClick={handleDecline}
              variant="danger"
              disabled={actionLoading}
              loading={actionLoading}
              icon={<XCircle size={20} />}
            >
              Decline Application
            </Button>
          </div>
        </div>
      )}

      {application.status === ApplicationStatus.APPROVED && (
        <div className="action-card">
          <h3>Disburse Loan</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="interestRate">Interest Rate (%) *</label>
              <input
                type="number"
                id="interestRate"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                disabled={actionLoading}
                step="0.01"
                min={application.product?.min_interest_rate}
                max={application.product?.max_interest_rate}
                required
              />
              {application.product && (
                <small>
                  Range: {parseFloat(application.product.min_interest_rate).toFixed(2)}% -{' '}
                  {parseFloat(application.product.max_interest_rate).toFixed(2)}%
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="disbursementDate">Disbursement Date</label>
              <input
                type="date"
                id="disbursementDate"
                value={disbursementDate}
                onChange={(e) => setDisbursementDate(e.target.value)}
                disabled={actionLoading}
              />
            </div>
          </div>

          <Button onClick={handleDisburse} variant="primary" disabled={actionLoading} loading={actionLoading} icon={<CheckCircle size={20} />}>
            Disburse Loan
          </Button>
        </div>
      )}
    </div>
  );
};
